import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload as UploadIcon, Download, Trash2, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = ["Daily Report", "Inspection", "Photos", "Client Document", "Other"];

export default function Uploads() {
  const { user } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState({ project_id: "all", category: "all" });
  const [form, setForm] = useState({ project_id: "", category: "", notes: "", file: null });
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    const params = {};
    if (filter.project_id && filter.project_id !== "all") params.project_id = filter.project_id;
    if (filter.category && filter.category !== "all") params.category = filter.category;
    const [u, p] = await Promise.all([api.get("/uploads", { params }), api.get("/projects")]);
    setUploads(u.data); setProjects(p.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter.project_id, filter.category]);

  const upload = async () => {
    if (!form.file) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("file", form.file);
    if (form.project_id) fd.append("project_id", form.project_id);
    if (form.category) fd.append("category", form.category);
    if (form.notes) fd.append("notes", form.notes);
    setUploading(true);
    try {
      await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setForm({ project_id: "", category: "", notes: "", file: null });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const download = async (id, name) => {
    const r = await api.get(`/uploads/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try { await api.delete(`/uploads/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="uploads-page">
      <div>
        <div className="label-eyebrow">REPORT UPLOAD PORTAL</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Files</h1>
      </div>

      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
        <div className="label-eyebrow mb-4">NEW UPLOAD</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="label-eyebrow">PROJECT</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="upload-project" className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">CATEGORY</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">FILE</Label>
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
              onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })}
              data-testid="upload-file"
              className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono text-xs"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-1 flex items-end">
            <Button onClick={upload} disabled={uploading} data-testid="upload-submit" className="w-full h-10 rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
              <UploadIcon className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <Label className="label-eyebrow">NOTES</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm" rows={2} />
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-4 flex gap-3 items-end">
        <div className="min-w-[180px]">
          <Label className="label-eyebrow">PROJECT</Label>
          <Select value={filter.project_id} onValueChange={(v) => setFilter({ ...filter, project_id: v })}>
            <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
              <SelectItem value="all">All</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Label className="label-eyebrow">CATEGORY</Label>
          <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
            <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
              <SelectItem value="all">All</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-x-auto">
        <table className="w-full text-sm" data-testid="uploads-table">
          <thead>
            <tr className="text-[#666] label-eyebrow border-b border-[#1c1c1c]">
              <th className="px-4 py-3 text-left font-normal">FILE</th>
              <th className="px-4 py-3 text-left font-normal">PROJECT</th>
              <th className="px-4 py-3 text-left font-normal">CATEGORY</th>
              <th className="px-4 py-3 text-left font-normal">UPLOADED BY</th>
              <th className="px-4 py-3 text-left font-normal">DATE</th>
              <th className="px-4 py-3 text-right font-normal">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.id} className="border-t border-[#1c1c1c] hover:bg-[#101010]">
                <td className="px-4 py-3"><span className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#666]" /> {u.original_filename}</span></td>
                <td className="px-4 py-3">{projectName(u.project_id)}</td>
                <td className="px-4 py-3"><span className="label-eyebrow">{u.category || "—"}</span></td>
                <td className="px-4 py-3">{u.uploaded_by_name}</td>
                <td className="px-4 py-3 font-mono tabular text-xs">{new Date(u.uploaded_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => download(u.id, u.original_filename)} className="rounded-none h-7 px-2"><Download className="h-3 w-3" /></Button>
                    {user.role === "admin" && <Button size="sm" variant="ghost" onClick={() => remove(u.id)} className="rounded-none h-7 px-2 text-red-400"><Trash2 className="h-3 w-3" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
            {uploads.length === 0 && <tr><td colSpan={6} className="text-center py-10 font-mono text-[#666]">NO UPLOADS</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
